<script lang="ts" setup>
import { AbcIcon } from "@ordbogen-npm/abc-dsk";
import A11yDialog from "a11y-dialog";
import { computed, nextTick, onMounted, onUnmounted, type PropType, ref, watch } from "vue";

import TgIconButton from "@/abc-dsk/components/IconButton/TgIconButton.vue";
import { color as colorProp } from "@/abc-dsk/props";

import { CLASS_NAME_COMPONENT as baseClass } from "./common";
import { size as sizeProp } from "./props";

type OptionalDecorationState = "show" | "hide";

const props = defineProps({
  size: sizeProp,
  color: colorProp,
  scrollLock: {
    type: Boolean,
    // eslint-disable-next-line vue/no-boolean-default
    default: true
  },
  lazy: {
    type: Boolean,
    // eslint-disable-next-line vue/no-boolean-default
    default: true
  },
  isOpen: {
    type: Boolean,
    default: false
  },
  alert: {
    type: Boolean,
    default: false
  },
  ariaCloseLabel: {
    type: String,
    default: "Luk dialogvinduet"
  },
  header: {
    type: String as PropType<OptionalDecorationState | "compact">,
    default: "show"
  },
  footer: {
    type: String as PropType<OptionalDecorationState>,
    default: "hide"
  },
  preventClosing: {
    type: Boolean,
    default: false
  },
  noFade: {
    type: Boolean,
    default: false
  },

  class: {
    type: [String, Object, Array],
    default: ""
  },
  modalClass: {
    type: [String, Object, Array],
    default: ""
  },
  dialogClass: {
    type: [String, Object, Array],
    default: ""
  },
  headerClass: {
    type: [String, Object, Array],
    default: ""
  },
  contentClass: {
    type: [String, Object, Array],
    default: ""
  },
  footerClass: {
    type: [String, Object, Array],
    default: ""
  },
  bodyClass: {
    type: [String, Object, Array],
    default: ""
  }
});

const emit = defineEmits<{
  (e: "close"): void;
}>();

const canEmit = ref(true);
const withoutEmit = (cb: () => void) => {
  canEmit.value = false;
  cb();
  canEmit.value = true;
};

// Data
let $dialogElement: A11yDialog | null = null;
const root = ref<HTMLElement | null>(null);
const isOpen = ref<boolean>(props.isOpen);
const isCreatedState = ref<boolean>(false);

// Computed properties
const modalRole = computed<"alertdialog" | "dialog">(() =>
  props.preventClosing || props.alert ? "alertdialog" : "dialog"
);

const rootClasses = computed<(string | object)[]>(() => [
  props.class,
  baseClass,
  props.modalClass,
  {
    [`${baseClass}--open`]: isOpen.value,
    [`${baseClass}--show`]: isOpen.value && !props.noFade,
    [`${baseClass}--fade`]: !props.noFade
  }
]);

const headerClasses = computed<(string | object)[]>(() => [
  props.headerClass,
  `${baseClass}__header`,
  `${baseClass}__header--${props.color}`,
  {
    [`${baseClass}__header--compact`]: props.header === "compact"
  }
]);

// Methods
const disableBodyScroll = (): void => {
  document.body.classList.add(`${baseClass}-open`);
};

const enableBodyScroll = (): void => {
  document.body.classList.remove(`${baseClass}-open`);
};

async function createDialog(): Promise<void> {
  if (isCreatedState.value) {
    return;
  }

  isCreatedState.value = true;
  // As the root element depends on isCreatedState = true, we need to wait for that state change to go through.
  await nextTick();

  if (!root.value) {
    return;
  }

  $dialogElement = new A11yDialog(root.value);

  $dialogElement.on("destroy", () => {
    $dialogElement = null;
    isCreatedState.value = false;

    if (props.scrollLock) {
      enableBodyScroll();
    }
  });

  $dialogElement.on("show", async () => {
    isOpen.value = true;

    if (props.scrollLock) {
      disableBodyScroll();
    }

    await nextTick();
    root.value?.focus();
  });

  $dialogElement.on("hide", () => {
    isOpen.value = false;

    if (props.scrollLock) {
      enableBodyScroll();
    }

    if (!props.lazy) {
      isCreatedState.value = false;
    }

    if (canEmit.value) {
      emit("close");
    }
  });
}

async function open() {
  await createDialog();
  $dialogElement?.show();
}

function close(force = false) {
  if (props.preventClosing && !force) {
    return;
  }

  if (!$dialogElement || !isOpen.value) {
    return;
  }

  $dialogElement.hide();
}

const onBackdropClick = () => {
  if (modalRole.value === "dialog") {
    close();
  }
};

// Hooks
onMounted(async (): Promise<void> => {
  if (isOpen.value) {
    await open();
  }
});
onUnmounted(() => {
  $dialogElement?.destroy();
});

watch(
  () => props.isOpen,
  (value) => {
    if (value === true) {
      return open();
    }

    // When closing the model via isOpen change, we don't want to emit 'close'
    // as that could trigger logic twice if using same function for setting `isOpen = false` and @close="closeModal()"
    withoutEmit((): void => close(true));
  }
);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isCreatedState"
      v-bind="$attrs"
      ref="root"
      :class="rootClasses"
      aria-modal="true"
      :aria-hidden="!isOpen"
      :role="modalRole"
    >
      <div :class="`${baseClass}__backdrop`" @click="onBackdropClick()" />
      <div
        :class="[
          `${baseClass}__dialog`,
          `${baseClass}__dialog--size-${props.size}`,
          props.dialogClass
        ]"
        role="document"
      >
        <div
          :class="[
            `${baseClass}__content`,
            `${baseClass}__content--${props.color}`,
            props.contentClass
          ]"
        >
          <div v-show="props.header !== 'hide'" :class="headerClasses">
            <div :class="`${baseClass}__header-slot`">
              <slot name="modal-header" />
            </div>

            <TgIconButton
              v-if="!props.preventClosing"
              color="grey"
              variant="negative"
              :class="`${baseClass}__close`"
              :aria-label="props.ariaCloseLabel"
              @click.prevent="close()"
            >
              <AbcIcon name="cross" color="white" />
            </TgIconButton>
          </div>

          <div :class="`${baseClass}__body-footer-container`">
            <div :class="[`${baseClass}__body`, props.bodyClass]">
              <slot />
            </div>

            <div
              v-show="props.footer !== 'hide'"
              :class="[`${baseClass}__footer`, props.footerClass]"
            >
              <slot name="modal-footer" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

@keyframes fade-in {
  from {
    opacity: 0;
  }
}

@keyframes slide-down {
  from {
    transform: translateY(-10%);
  }
}

.tg-modal-open {
  overflow: hidden;
}

.tg-modal {
  display: none;
  position: fixed;
  z-index: 1050;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;

  &--open {
    display: block;
    overflow-x: hidden;
    overflow-y: auto;
  }

  &--fade {
    transition: opacity 150ms linear;
    opacity: 0;
  }

  &--show {
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    * {
      transition: none;
      animation: none;
    }
  }

  &__backdrop {
    position: fixed;
    z-index: 1040;
    top: 0;
    left: 0;
    width: 100%;
    min-width: 100vw;
    height: 100%;
    min-height: 100vh;
    animation: fade-in 250ms both;
    background-color: rgba(@colors[@black], 0.4);
  }

  &__dialog {
    display: flex;
    position: relative;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: calc(100vw - 5px * 2);
    min-height: calc(100% - 1rem);
    margin: 24px auto;
    pointer-events: none;

    &--size-sm {
      .tg-modal__content {
        max-width: 360px;
      }

      .tg-modal__body {
        padding: 15px;

        #media-breakpoint-up(sm, {
          padding: 20px;
        });

        #media-breakpoint-up(md, {
          padding: 30px;
        });
      }
    }

    &--size-md {
      .tg-modal__content {
        max-width: 680px;
      }

      .tg-modal__body {
        padding: 15px;

        #media-breakpoint-up(sm, {
          padding: 30px;
        });

        #media-breakpoint-up(md, {
          padding: 40px;
        });
      }
    }

    &--size-lg {
      .tg-modal__content {
        max-width: 840px;
      }

      .tg-modal__body {
        padding: 15px;

        #media-breakpoint-up(md, {
          padding: 50px;
        });
      }
    }

    &--size-xl {
      .tg-modal__content {
        max-width: 1080px;
      }

      .tg-modal__body {
        padding: 15px;

        #media-breakpoint-up(md, {
          padding: 50px;
        });
      }
    }

    &::before {
      content: "";
      display: block;
      height: calc(100vh - 1rem); // fallback
      height: min-content; /* stylelint-disable-line declaration-block-no-duplicate-properties */
    }

    #media-breakpoint-up(sm, {
      min-height: calc(100% - 3.5rem);

      &::before {
        height: calc(100vh - 3.5rem); // fallback
        height: min-content; /* stylelint-disable-line declaration-block-no-duplicate-properties */
      }
    });
  }

  &__content {
    display: flex;
    position: relative;
    z-index: 1050;
    flex-direction: column;
    width: 100%;
    animation:
      fade-in 150ms both,
      slide-down 150ms both;
    color: rgb(@theme-colors[@grey][110]);
    pointer-events: auto;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 46px;
    padding: 9px 9px 9px 17px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    color: @colors[@white];

    &--compact {
      min-height: 34px;
    }

    each(@theme-colors, {
      @name: replace(@key, "@", "");

      &--@{name} {
        background-color: rgb(@value[base]);
      }
    });
  }

  &__header-slot {
    display: flex;
    align-items: center;
  }

  &__body-footer-container {
    border-bottom-right-radius: 8px;
    border-bottom-left-radius: 8px;
    background-color: @colors[@white];
  }

  &__body {
    position: relative;
    flex: 1 1 auto;
  }

  &__footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    padding: 12px;
    border-top: 1px solid rgb(@theme-colors[@grey][50]);
  }
}
</style>
